import { MenuProxy } from '@proxies/menu'
import { useAppStore } from '@store'
import { useEffect, useState } from 'react'

export type MenuData = {
  altoicono: null
  anchoicono: null
  clavemenu: string
  submenus: Submenu[]
}

export type Submenu = {
  accion: null | string
  alto: null | number
  altoicono: null | number
  ancho: null | number
  anchoicono: null | number
  args: null | string
  claveMenuItem: string
  clavemenuabrir: string
  codigoUnidad: null | string
  descripcion: null | string
  descripcionLarga: null | string
  fondo: null | string
  icono: string
  id: number
  idUnidades: null | number
  morphs: null | any
  orden: number
  referencia: null | string
  texto: string
}

// Menús donde NO debemos aplicar el filtro armazonBlanco quieren los null
const MENUS_SIN_FILTRO = [
  'ESTANTESMENU',
  'ESTANTESGOLAMENU', // ESTANTES GOLA también debe estar sin filtro
  'KITSESTANDARMENU',
  'COSTADOSYLATERALESMENU',
  'REGLETAMENU',
  'COSTADOSMENU',
  'FORROHORIZALTOMENU',
  'LATERALESMENU',
]

export function useFetchMenu(
  key: string,
  dependency: any = null,
  acabadoBlanco: boolean | null = null,
) {
  const [menuData, setMenuData] = useState<MenuData>()
  const idiomaCultura = useAppStore((s) => s.idiomaCultura)
  const isModalOpen = useAppStore((s) => s.isModalOpen)

  useEffect(() => {
    // Leer el token directamente dentro del useEffect para asegurar que siempre tenemos el valor más reciente
    const token = sessionStorage.getItem('access_token')

    // Verificar si hay token antes de ejecutar getMenuData
    if (!token) {
      // No hacer nada si no hay token - el useEffect se re-ejecutará cuando el token esté disponible
      return
    }

    if (isModalOpen) {
      return
    }

    async function getMenuData() {
      try {
        const menuProxy = new MenuProxy()
        const input: any = { clavemenu: key }

        if (acabadoBlanco !== null && !MENUS_SIN_FILTRO.includes(key)) {
          input.filtro = { armazonBlanco: acabadoBlanco }
        }

        const data: MenuData = await menuProxy.getMenu(input, idiomaCultura)
        const sortedSubmenus = Array.isArray(data?.submenus)
          ? [...data.submenus].sort((a, b) => {
              const orderA = Number(a?.orden ?? Number.MAX_SAFE_INTEGER)
              const orderB = Number(b?.orden ?? Number.MAX_SAFE_INTEGER)
              if (orderA !== orderB) return orderA - orderB
              return String(a?.texto || a?.claveMenuItem || '').localeCompare(
                String(b?.texto || b?.claveMenuItem || ''),
                'es',
              )
            })
          : []
        setMenuData(() => {
          return {
            ...data,
            submenus: sortedSubmenus,
          }
        })
      } catch (error: any) {
        // Manejar errores 401 (Unauthorized) - token expirado o inválido
        if (error?.status === 401) {
          console.warn('[useFetchMenu] Error 401: Token de autenticación expirado o inválido', {
            menuKey: key,
          })
          // No establecer menuData para evitar actualizaciones innecesarias
          return
        }
        // Para otros errores, loguear pero no propagar
        console.error('[useFetchMenu] Error al obtener datos del menú:', error)
      }
    }

    getMenuData()
  }, [navigator.language, idiomaCultura, key, isModalOpen, acabadoBlanco])

  return {
    menuData,
  }
}
